import * as R from 'ramda';
import useLocalStorage from '@rehooks/local-storage';
import { useCallback, useMemo } from 'react';
import useTechSkills from './useTechSkills';
import { Profile } from '../types';
import { TechnicalSkill } from '../generated/graphql';

type TechnicalSkillSummary = Pick<TechnicalSkill, 'id' | 'name'>;

type ProfileStored = Omit<Profile, 'skills'> & {
  skills?: TechnicalSkillSummary[];
};

function mapToFullSkills(skills: TechnicalSkillSummary[], fullSkills?: TechnicalSkill[]) {
  if (!fullSkills || !skills) return [];
  const mapped = R.map((sk: TechnicalSkillSummary) => R.find<TechnicalSkill>(R.propEq('id', sk.id))(fullSkills))(skills);
  return R.filter(Boolean, mapped) as TechnicalSkill[];
}

function mapToSummarySkills(fullSkills?: TechnicalSkill[]): TechnicalSkillSummary[] | undefined {
  if (!fullSkills) return undefined;
  return R.map(R.pick(['id', 'name']), fullSkills);
}

export default function useProfileStorage() {
  const { fetchingSkills, skills: fullSkills } = useTechSkills();
  const [profile, writeProfileToLS, deleteProfile] = useLocalStorage<ProfileStored>('profile');
  const writeProfile = useCallback(
    (profileToStore: Profile) => writeProfileToLS({
      ...profileToStore,
      skills: mapToSummarySkills(profileToStore.skills),
    }),
    [writeProfileToLS],
  );
  const profileWithDefaults = useMemo(
    () => ({
      firstName: profile?.firstName ?? '',
      role: profile?.role ?? '',
      yearsOfExperience: profile?.yearsOfExperience,
      grossMonthlyCompensation: profile?.grossMonthlyCompensation ?? '',
      country: profile?.country || null,
      company: {
        country: profile?.company?.country || null,
        numberOfEmployees: profile?.company?.numberOfEmployees ?? '',
        createdAtYear: profile?.company?.createdAtYear ?? '',
        financingAmount: profile?.company?.financingAmount ?? '',
        name: profile?.company?.name || '',
      },
      skills: mapToFullSkills(
        profile?.skills ?? [],
        fullSkills,
      ),
    } as Required<Profile>),
    [profile, fullSkills],
  );
  return {
    profile: profileWithDefaults,
    writeProfile,
    deleteProfile,
    fetchingProfile: fetchingSkills,
  };
}
